import React, { useState, useEffect, useRef } from 'react';
import { XStack, YStack, ScrollView, Spinner } from 'tamagui';
import { View, TouchableOpacity, Switch, Alert, Platform, Pressable } from 'react-native';
import { Card, Text, TextInput } from '@constants/Themed';
import { WebView } from 'react-native-webview';
import { Feather, MaterialIcons } from '@expo/vector-icons';
import DropDownPicker from 'react-native-dropdown-picker';
import LinkGenModal from '../DataScreen/CaptureScreen/Components/LinkGenModal';
import { useHasActiveSubscription } from '@utils/billingUtils';
import { KnowledgeGroup } from '../KnowledgeScreen/Components/GroupsFlatList';
import { MotiPressable } from 'moti/interactions';
import * as Animatable from 'react-native-animatable';
import { getApiBaseUrl } from '@api/constant';

interface ChatBotConfig {
  // Basic Settings
  title: string;
  description: string;
  welcomeMessage: string;

  // Behavior Settings
  systemPrompt: string;
  responseStyle: 'concise' | 'detailed' | 'step-by-step';
  memoryEnabled: boolean;
  autoSummarize: boolean;

  // Parameters
  temperature: number;
  maxTokens: number;
  frequencyPenalty: number;
  presencePenalty: number;

  // Appearance
  theme: {
    primaryColor: string;
    backgroundColor: string;
    textColor: string;
    borderRadius: number;
  };
  position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

  // Behavior
  behavior: {
    maxMessages: number;
    typingDelay: number;
    autoScroll: boolean;
    showTimestamp: boolean;
    allowFileUpload: boolean;
  };

  // AI Settings
  ai: {
    temperature: number;
    maxTokens: number;
    includeKnowledgeBase: boolean;
    includePublicKnowledge: boolean;
    customInstructions: string;
  };

  // Integration
  integration: {
    enabled: boolean;
    showPoweredBy: boolean;
    allowEmbedding: boolean;
    allowedDomains: string[];
  };

  // Advanced
  stream: 'sse' | 'ws' | 'none';
  userId?: string;
  companyId?: string;

  // Knowledge Base Settings
  knowledgeBase: {
    scope: 'entire' | 'groups';
    linkedTo: 'userId' | 'companyId';
    selectedGroups?: string[]; // Array of group IDs if scope is 'groups'
  };
}

const defaultConfig: ChatBotConfig = {
  title: 'SmartSaaS Assistant',
  description: 'Your intelligent AI assistant',
  welcomeMessage: 'Hello! How can I help you today?',
  systemPrompt: 'You are a helpful AI assistant. Provide accurate, concise, and helpful responses.',
  responseStyle: 'concise',
  memoryEnabled: false,
  autoSummarize: false,
  temperature: 0.5,
  maxTokens: 2048,
  frequencyPenalty: 0,
  presencePenalty: 0,
  theme: {
    primaryColor: '#667eea',
    backgroundColor: '#ffffff',
    textColor: '#333333',
    borderRadius: 8
  },
  position: 'bottom-right',
  behavior: {
    maxMessages: 50,
    typingDelay: 1000,
    autoScroll: true,
    showTimestamp: false,
    allowFileUpload: false
  },
  ai: {
    temperature: 0.5,
    maxTokens: 2048,
    includeKnowledgeBase: true,
    includePublicKnowledge: true,
    customInstructions: ''
  },
  integration: {
    enabled: true,
    showPoweredBy: true,
    allowEmbedding: true,
    allowedDomains: []
  },
  stream: 'none',
  knowledgeBase: {
    scope: 'entire',
    linkedTo: 'userId',
    selectedGroups: []
  }
};

interface ChatBotConfigItem {
  _id: string;
  title: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

const ChatBotScreen = () => {
  const [config, setConfig] = useState<ChatBotConfig>(defaultConfig);
  const [previewKey, setPreviewKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(false);
  const [configs, setConfigs] = useState<ChatBotConfigItem[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<string | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const webViewRef = useRef<WebView>(null);
  const linkGenModalRef = useRef<any>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const hasActiveSubscription = useHasActiveSubscription();
  const [saveStatus, setSaveStatus] = useState<{ success?: boolean; error?: boolean } | null>(null);

  // Load knowledge groups from localStorage
  const [knowledgeGroups, setKnowledgeGroups] = useState<KnowledgeGroup[]>(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('knowledgeGroups');
        if (saved) {
          return JSON.parse(saved);
        }
      } catch (e) {
        console.error('Failed to load knowledge groups from localStorage:', e);
      }
    }
    return [];
  });

  // Update preview when config changes
  useEffect(() => {
    // Force WebView refresh when config changes
    setPreviewKey(prev => prev + 1);
  }, [config]);

  const updateConfig = (key: keyof ChatBotConfig, value: any) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const generateEmbedHTML = () => {
    const embedConfig = {
      apiBase: process.env.EXPO_PUBLIC_API_URL,
      theme: config.theme.primaryColor,
      position: config.position,
      chatId: 'preview',
      userId: 'preview-user',
      companyId: 'preview-company',
      locale: 'en',
      title: config.title,
      welcomeMessage: config.welcomeMessage
    };

    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>ChatBot Preview</title>
          <style>
            * {
              box-sizing: border-box;
            }
            html, body {
              margin: 0;
              padding: 0;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              background: #f8fafc;
              height: 100%;
              overflow: hidden;
            }
            .preview-container {
              height: 100vh;
              width: 100%;
              display: flex;
              flex-direction: column;
            }
            .preview-content {
              flex: 1;
              position: relative;
              background: white;
              min-height: 0;
              overflow: hidden;
              height: 100%;
            }
          </style>
        </head>
        <body>
          <div class="preview-container">
            <div class="preview-content">
              <!-- ChatBot will be embedded here -->
            </div>
          </div>
          
          <!-- SmartSaaS Chat Widget Embed Script -->
          <script>
            (function () {
              'use strict';
              
              const DEFAULTS = {
                apiBase: '${embedConfig.apiBase}',
                theme: '${embedConfig.theme}',
                position: '${embedConfig.position}',
                chatId: '${embedConfig.chatId}',
                userId: '${embedConfig.userId}',
                companyId: '${embedConfig.companyId}',
                locale: '${embedConfig.locale}',
                title: '${embedConfig.title}',
                welcomeMessage: '${embedConfig.welcomeMessage}'
              };
              
              // Create chat window for preview
              function createChatWindow(config) {
                const chatWindow = document.createElement('div');
                
                chatWindow.style.cssText = \`
                  position: absolute;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  width: 100%;
                  height: 100%;
                  background: rgba(255, 255, 255, 0.95);
                  backdrop-filter: blur(20px);
                  display: flex;
                  flex-direction: column;
                  overflow: hidden;
                \`;
                
                // Create header
                const header = document.createElement('div');
                header.style.cssText = \`
                  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                  color: white;
                  padding: 20px;
                  display: flex;
                  justify-content: space-between;
                  align-items: flex-start;
                  font-weight: 600;
                  font-size: 16px;
                  position: relative;
                  overflow: hidden;
                \`;
                
                // Create title container with powered by text
                const titleContainer = document.createElement('div');
                titleContainer.style.cssText = \`
                  display: flex;
                  flex-direction: column;
                  align-items: flex-start;
                  gap: 2px;
                \`;
                
                const title = document.createElement('span');
                title.textContent = config.title;
                title.style.cssText = \`
                  font-size: 16px;
                  font-weight: 600;
                \`;
                
                ${config.integration.showPoweredBy ? `
                const poweredBy = document.createElement('div');
                poweredBy.style.cssText = \`
                  display: flex;
                  align-items: center;
                  gap: 4px;
                  font-size: 10px;
                \`;
                
                const poweredByText = document.createElement('span');
                poweredByText.textContent = 'Powered by';
                poweredByText.style.cssText = \`
                  color: rgba(255, 255, 255, 0.7);
                  font-size: 10px;
                  font-weight: 400;
                \`;
                
                const poweredByBrand = document.createElement('span');
                poweredByBrand.textContent = 'SmartSaaS';
                poweredByBrand.style.cssText = \`
                  color: rgba(255, 255, 255, 0.8);
                  font-size: 10px;
                  font-weight: 600;
                \`;
                
                poweredBy.appendChild(poweredByText);
                poweredBy.appendChild(poweredByBrand);
                titleContainer.appendChild(poweredBy);
                ` : ''}
                
                titleContainer.appendChild(title);
                
                const closeBtn = document.createElement('button');
                closeBtn.innerHTML = '✕';
                closeBtn.style.cssText = \`
                  background: rgba(255, 255, 255, 0.1);
                  border: 1px solid rgba(255, 255, 255, 0.2);
                  color: white;
                  font-size: 16px;
                  cursor: pointer;
                  padding: 8px;
                  border-radius: 8px;
                  transition: all 0.3s ease;
                  backdrop-filter: blur(10px);
                  width: 32px;
                  height: 32px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                \`;
                
                header.appendChild(titleContainer);
                header.appendChild(closeBtn);
                
                // Create messages area
                const messagesArea = document.createElement('div');
                messagesArea.style.cssText = \`
                  flex: 1;
                  overflow-y: auto;
                  padding: 16px;
                  display: flex;
                  flex-direction: column;
                  gap: 12px;
                  background: linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%);
                  min-height: 0;
                  height: 100%;
                \`;
                
                // Create input area
                const inputArea = document.createElement('div');
                inputArea.style.cssText = \`
                  display: flex;
                  padding: 16px;
                  gap: 12px;
                  border-top: 1px solid rgba(102, 126, 234, 0.1);
                  align-items: center;
                  background: rgba(255, 255, 255, 0.95);
                  backdrop-filter: blur(10px);
                \`;
                
                const input = document.createElement('input');
                input.type = 'text';
                input.placeholder = 'Type your message...';
                input.style.cssText = \`
                  flex: 1;
                  padding: 12px 16px;
                  border-radius: 16px;
                  border: 2px solid rgba(102, 126, 234, 0.2);
                  font-size: 14px;
                  background: rgba(255, 255, 255, 0.9);
                  backdrop-filter: blur(5px);
                  transition: all 0.3s ease;
                  outline: none;
                \`;
                
                const sendBtn = document.createElement('button');
                sendBtn.textContent = 'Send';
                sendBtn.style.cssText = \`
                  padding: 12px 20px;
                  border-radius: 16px;
                  border: none;
                  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                  color: white;
                  cursor: pointer;
                  font-weight: 600;
                  font-size: 14px;
                  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
                  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
                  min-width: 80px;
                \`;
                
                inputArea.appendChild(input);
                inputArea.appendChild(sendBtn);
                
                // Add welcome message
                const welcomeMsg = document.createElement('div');
                welcomeMsg.textContent = config.welcomeMessage;
                welcomeMsg.style.cssText = \`
                  max-width: 85%;
                  padding: 12px 16px;
                  border-radius: 18px;
                  line-height: 1.4;
                  word-wrap: break-word;
                  align-self: flex-start;
                  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%);
                  color: #374151;
                  border: 1px solid rgba(229, 231, 235, 0.5);
                  border-bottom-left-radius: 6px;
                  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                \`;
                
                messagesArea.appendChild(welcomeMsg);
                
                // Assemble chat window
                chatWindow.appendChild(header);
                chatWindow.appendChild(messagesArea);
                chatWindow.appendChild(inputArea);
                
                // Store references
                chatWindow.closeBtn = closeBtn;
                chatWindow.input = input;
                chatWindow.sendBtn = sendBtn;
                chatWindow.messagesArea = messagesArea;
                
                return chatWindow;
              }
              
              // Initialize preview
              const chatWindow = createChatWindow(DEFAULTS);
              document.querySelector('.preview-content').appendChild(chatWindow);
              
              // Create floating button
              function createFloatingButton() {
                const button = document.createElement('button');
                button.innerHTML = '💬';
                button.style.cssText = \`
                  position: fixed;
                  bottom: 20px;
                  right: 20px;
                  width: 64px;
                  height: 64px;
                  border-radius: 32px;
                  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                  color: white;
                  border: none;
                  font-size: 28px;
                  cursor: pointer;
                  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
                  transition: all 0.4s ease;
                  z-index: 1000;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  backdrop-filter: blur(10px);
                \`;
                
                button.addEventListener('mouseenter', function () {
                  this.style.transform = 'translateY(-2px) scale(1.05)';
                  this.style.boxShadow = '0 12px 35px rgba(102, 126, 234, 0.5)';
                });
                
                button.addEventListener('mouseleave', function () {
                  this.style.transform = 'translateY(0) scale(1)';
                  this.style.boxShadow = '0 8px 25px rgba(102, 126, 234, 0.4)';
                });
                
                return button;
              }
              
              const floatingButton = createFloatingButton();
              document.querySelector('.preview-content').appendChild(floatingButton);
              floatingButton.style.display = 'none';
              
              // Add demo functionality
              let isOpen = true;
              
              chatWindow.closeBtn.addEventListener('click', function () {
                if (isOpen) {
                  chatWindow.style.display = 'none';
                  floatingButton.style.display = 'flex';
                  isOpen = false;
                }
              });
              
              floatingButton.addEventListener('click', function () {
                if (!isOpen) {
                  chatWindow.style.display = 'flex';
                  floatingButton.style.display = 'none';
                  isOpen = true;
                }
              });
              
              chatWindow.sendBtn.addEventListener('click', function () {
                const message = chatWindow.input.value.trim();
                if (!message) return;
                
                // Add user message
                const userMsg = document.createElement('div');
                userMsg.textContent = message;
                userMsg.style.cssText = \`
                  max-width: 85%;
                  padding: 12px 16px;
                  border-radius: 18px;
                  line-height: 1.4;
                  word-wrap: break-word;
                  align-self: flex-end;
                  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
                  color: #1e293b;
                  border: 1px solid rgba(102, 126, 234, 0.2);
                  border-bottom-right-radius: 6px;
                  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                \`;
                
                chatWindow.messagesArea.appendChild(userMsg);
                chatWindow.input.value = '';
                
                // Simulate bot response
                setTimeout(() => {
                  const responses = {
                    concise: \`Based on your question about "\${message}", here's a concise answer.\`,
                    detailed: \`Thank you for your question about "\${message}". Let me provide you with a comprehensive response that covers all the important aspects...\`,
                    'step-by-step': \`To address your question about "\${message}", let me break this down into steps:\\n\\n1. First, we need to understand the context\\n2. Then, we can analyze the requirements\\n3. Finally, we can provide a solution\`
                  };
                  
                  const botMsg = document.createElement('div');
                  botMsg.textContent = responses['${config.responseStyle}'];
                  botMsg.style.cssText = \`
                    max-width: 85%;
                    padding: 12px 16px;
                    border-radius: 18px;
                    line-height: 1.4;
                    word-wrap: break-word;
                    align-self: flex-start;
                    background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.9) 100%);
                    color: #374151;
                    border: 1px solid rgba(229, 231, 235, 0.5);
                    border-bottom-left-radius: 6px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                  \`;
                  
                  chatWindow.messagesArea.appendChild(botMsg);
                  chatWindow.messagesArea.scrollTop = chatWindow.messagesArea.scrollHeight;
                }, 1000);
                
                chatWindow.messagesArea.scrollTop = chatWindow.messagesArea.scrollHeight;
              });
              
              chatWindow.input.addEventListener('keypress', function (e) {
                if (e.key === 'Enter') {
                  chatWindow.sendBtn.click();
                }
              });
              
            })();
          </script>
        </body>
      </html>
    `;
  };

  const loadConfigurationsList = async () => {
    setIsLoadingConfigs(true);
    try {
      const apiBaseUrl = await getApiBaseUrl();
      const url = `${apiBaseUrl}/knowledge/chatbot/configs`;
      
      console.log('📋 Loading configurations list:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      console.log('📋 Configs list response status:', response.status, response.statusText);

      if (!response.ok) {
        if (response.status === 404 || response.status === 403) {
          console.log('⚠️ No configs found or not authorized, starting with empty list');
          // No configs or not authorized, start with empty list
          setConfigs([]);
          return;
        }
        const errorText = await response.text().catch(() => '');
        console.error('❌ Error loading configurations list:', {
          status: response.status,
          statusText: response.statusText,
          errorText
        });
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('📋 Configs list result:', {
        success: result.success,
        configsCount: result.configs?.length || 0,
        configs: result.configs?.map((c: any) => ({ _id: c._id, title: c.title }))
      });
      
      if (result.success && result.configs) {
        setConfigs(result.configs);
        // If no config is selected and we have configs, select the first one
        if (!selectedConfigId && result.configs.length > 0) {
          console.log('🔄 Auto-selecting first config:', result.configs[0]._id);
          const firstConfigId = result.configs[0]._id;
          setSelectedConfigId(firstConfigId);
          // Load the config immediately to avoid race condition
          setTimeout(() => {
            loadConfiguration(firstConfigId);
          }, 100);
        }
      } else {
        console.warn('⚠️ Unexpected response format:', result);
        setConfigs([]);
      }
    } catch (error) {
      console.error('❌ Error loading configurations list:', error);
      setConfigs([]);
    } finally {
      setIsLoadingConfigs(false);
    }
  };

  const saveConfiguration = async () => {
    if (!config.title || config.title.trim() === '') {
      Alert.alert('Validation Error', 'Please enter a title for the chatbot configuration.');
      return;
    }

    setIsSaving(true);
    setSaveStatus(null); // Clear any previous status
    
    try {
      // Get API base URL using the same method as loadConfiguration
      const apiBaseUrl = await getApiBaseUrl();
      const url = `${apiBaseUrl}/knowledge/chatbot/config`;

      console.log('💾 Saving configuration:', {
        url,
        isCreatingNew,
        selectedConfigId,
        title: config.title
      });

      // Build request body
      const requestBody: any = {
        title: config.title,
        description: config.description,
        welcomeMessage: config.welcomeMessage,
        systemPrompt: config.systemPrompt,
        responseStyle: config.responseStyle,
        memoryEnabled: config.memoryEnabled,
        autoSummarize: config.autoSummarize,
        temperature: config.temperature,
        maxTokens: config.maxTokens,
        frequencyPenalty: config.frequencyPenalty,
        presencePenalty: config.presencePenalty,
        theme: config.theme,
        position: config.position,
        behavior: config.behavior,
        ai: config.ai,
        integration: config.integration,
        stream: config.stream,
        userId: config.userId,
        companyId: config.companyId,
        knowledgeBase: config.knowledgeBase
      };

      // Include _id if updating existing config (backend will update that specific config)
      if (selectedConfigId && !isCreatingNew) {
        requestBody._id = selectedConfigId;
      }

      console.log('📤 Request body:', JSON.stringify(requestBody, null, 2));

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      console.log('📥 Response status:', response.status, response.statusText);

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
          console.error('❌ Error response data:', errorData);
        } catch (e) {
          const errorText = await response.text().catch(() => '');
          console.error('❌ Error response text:', errorText);
          if (errorText) {
            errorMessage = errorText;
          }
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('✅ Configuration saved successfully:', result);
      
      // Set success status for button animation
      setSaveStatus({ success: true });
      
      // Reload configs list to get updated/new config
      await loadConfigurationsList();
      
      // If creating new, set the selected config to the newly created one
      if (result.config && result.config._id) {
        setSelectedConfigId(result.config._id);
        setIsCreatingNew(false);
      }

      // Clear success status after 3 seconds
      setTimeout(() => {
        setSaveStatus(null);
      }, 3000);
    } catch (error: any) {
      console.error('❌ Error saving configuration:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      
      setSaveStatus({ error: true });
      
      const errorMessage = error.message || 'Failed to save configuration. Please try again.';
      Alert.alert('Error', errorMessage);
      
      // Clear error status after 3 seconds
      setTimeout(() => {
        setSaveStatus(null);
      }, 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const deleteConfiguration = async (configId: string) => {
    Alert.alert(
      'Delete Configuration',
      'Are you sure you want to delete this chatbot configuration? This action cannot be undone.',
      [
        {
          text: 'Cancel',
          style: 'cancel'
        },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              const apiBaseUrl = await getApiBaseUrl();
              const response = await fetch(`${apiBaseUrl}/knowledge/chatbot/config/${configId}`, {
                method: 'DELETE',
                headers: {
                  'Content-Type': 'application/json',
                },
                credentials: 'include'
              });

              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }

              Alert.alert('Success', 'Configuration deleted successfully!');
              
              // If deleted config was selected, clear selection and reset to defaults
              if (selectedConfigId === configId) {
                setSelectedConfigId(null);
                setIsCreatingNew(false);
                setConfig(defaultConfig);
              }
              
              // Reload configs list
              await loadConfigurationsList();
            } catch (error) {
              console.error('Error deleting configuration:', error);
              Alert.alert('Error', 'Failed to delete configuration. Please try again.');
            }
          }
        }
      ]
    );
  };

  const loadConfiguration = async (configId?: string) => {
    setIsLoading(true);
    try {
      const apiBaseUrl = await getApiBaseUrl();
      const url = configId
        ? `${apiBaseUrl}/knowledge/chatbot/config/${configId}`
        : `${apiBaseUrl}/knowledge/chatbot/config`;

      console.log('📥 Loading configuration:', { configId, url });

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      console.log('📥 Response status:', response.status, response.statusText);

      if (!response.ok) {
        if (response.status === 404) {
          console.log('⚠️ No configuration found, using defaults');
          // No existing configuration, use defaults
          setConfig(defaultConfig);
          return;
        }
        const errorText = await response.text().catch(() => '');
        console.error('❌ Error loading configuration:', {
          status: response.status,
          statusText: response.statusText,
          errorText
        });
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('📥 Loaded configuration response:', result);
      
      if (result.success && result.config) {
        const savedConfig = result.config;
        console.log('📋 Merging saved config:', {
          hasSystemPrompt: !!savedConfig.systemPrompt,
          hasResponseStyle: !!savedConfig.responseStyle,
          hasKnowledgeBase: !!savedConfig.knowledgeBase,
          hasPosition: !!savedConfig.position,
          hasStream: !!savedConfig.stream,
          fullConfig: savedConfig
        });
        
        // Merge loaded config with default to ensure all fields are present
        const mergedConfig = {
          ...defaultConfig,
          ...savedConfig,
          // Deep merge nested objects
          theme: {
            ...defaultConfig.theme,
            ...(savedConfig.theme || {})
          },
          behavior: {
            ...defaultConfig.behavior,
            ...(savedConfig.behavior || {})
          },
          ai: {
            ...defaultConfig.ai,
            ...(savedConfig.ai || {})
          },
          integration: {
            ...defaultConfig.integration,
            ...(savedConfig.integration || {})
          },
          knowledgeBase: {
            ...defaultConfig.knowledgeBase,
            ...(savedConfig.knowledgeBase || {}),
            selectedGroups: savedConfig.knowledgeBase?.selectedGroups || []
          },
          // Ensure top-level fields are set
          systemPrompt: savedConfig.systemPrompt || defaultConfig.systemPrompt,
          responseStyle: savedConfig.responseStyle || defaultConfig.responseStyle,
          memoryEnabled: savedConfig.memoryEnabled !== undefined ? savedConfig.memoryEnabled : defaultConfig.memoryEnabled,
          autoSummarize: savedConfig.autoSummarize !== undefined ? savedConfig.autoSummarize : defaultConfig.autoSummarize,
          temperature: savedConfig.temperature !== undefined ? savedConfig.temperature : (savedConfig.ai?.temperature !== undefined ? savedConfig.ai.temperature : defaultConfig.temperature),
          maxTokens: savedConfig.maxTokens !== undefined ? savedConfig.maxTokens : (savedConfig.ai?.maxTokens !== undefined ? savedConfig.ai.maxTokens : defaultConfig.maxTokens),
          frequencyPenalty: savedConfig.frequencyPenalty !== undefined ? savedConfig.frequencyPenalty : defaultConfig.frequencyPenalty,
          presencePenalty: savedConfig.presencePenalty !== undefined ? savedConfig.presencePenalty : defaultConfig.presencePenalty,
          position: savedConfig.position || defaultConfig.position,
          stream: savedConfig.stream || defaultConfig.stream,
          userId: savedConfig.userId,
          companyId: savedConfig.companyId
        };
        
        console.log('✅ Merged config:', {
          title: mergedConfig.title,
          hasSystemPrompt: !!mergedConfig.systemPrompt,
          hasKnowledgeBase: !!mergedConfig.knowledgeBase,
          knowledgeBaseScope: mergedConfig.knowledgeBase?.scope,
          selectedGroups: mergedConfig.knowledgeBase?.selectedGroups
        });
        
        setConfig(mergedConfig);
      } else if (result.config) {
        // Handle case where config is returned directly (backward compatibility)
        const savedConfig = result.config;
        const mergedConfig = {
          ...defaultConfig,
          ...savedConfig,
          theme: {
            ...defaultConfig.theme,
            ...(savedConfig.theme || {})
          },
          behavior: {
            ...defaultConfig.behavior,
            ...(savedConfig.behavior || {})
          },
          ai: {
            ...defaultConfig.ai,
            ...(savedConfig.ai || {})
          },
          integration: {
            ...defaultConfig.integration,
            ...(savedConfig.integration || {})
          },
          knowledgeBase: {
            ...defaultConfig.knowledgeBase,
            ...(savedConfig.knowledgeBase || {}),
            selectedGroups: savedConfig.knowledgeBase?.selectedGroups || []
          }
        };
        setConfig(mergedConfig);
      } else {
        console.warn('⚠️ No config in response, using defaults');
        setConfig(defaultConfig);
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
      // Don't show error for loading, just use defaults silently
      setConfig(defaultConfig);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfigSelect = (configId: string | null) => {
    // Check if user can create multiple configs (paid users only)
    if (configId === 'new') {
      if (!hasActiveSubscription && configs.length > 0) {
        Alert.alert(
          'Multiple Configurations',
          'Multiple chatbot configurations are available for paid users only. Please upgrade your plan to create additional configurations.',
          [{ text: 'OK' }]
        );
        return;
      }
      // Create new configuration
      setSelectedConfigId(null);
      setIsCreatingNew(true);
      setConfig(defaultConfig);
    } else if (configId) {
      // Load existing configuration
      setSelectedConfigId(configId);
      setIsCreatingNew(false);
      loadConfiguration(configId);
    } else {
      // Clear selection
      setSelectedConfigId(null);
      setIsCreatingNew(false);
      setConfig(defaultConfig);
    }
  };

  const resetConfiguration = () => {
    setConfig(defaultConfig);
    Alert.alert('Reset', 'Configuration reset to defaults');
  };

  const generateChatBotEmbedCode = (type: 'script' | 'iframe', width: string, height: string) => {
    const embedConfig: any = {
      apiBase: 'https://api.smartsaas.pro',
      title: config.title,
      welcomeMessage: config.welcomeMessage,
      theme: config.theme.primaryColor,
      position: config.position,
      responseStyle: config.responseStyle,
      systemPrompt: config.systemPrompt,
      memoryEnabled: config.memoryEnabled,
      autoSummarize: config.autoSummarize,
      temperature: config.temperature,
      maxTokens: config.maxTokens,
      showPoweredBy: config.integration.showPoweredBy,
      stream: config.stream
    };

    // Include configId if available (for multiple chatbot support)
    if (selectedConfigId && !isCreatingNew) {
      embedConfig.configId = selectedConfigId;
    }

    if (type === 'script') {
      return `<!-- SmartSaaS ChatBot Widget -->
<script>
  (function () {
    'use strict';
    
    const config = ${JSON.stringify(embedConfig, null, 2)};
    
    // Create and inject the chatbot widget
    const script = document.createElement('script');
    script.src = 'https://api.smartsaas.pro/embed/chatbot.js';
    script.async = true;
    script.onload = function() {
      if (window.SmartSaaSChatBot) {
        window.SmartSaaSChatBot.init(config);
      }
    };
    
    document.head.appendChild(script);
  })();
</script>`;
    } else {
      return `<iframe 
  src="https://api.smartsaas.pro/embed/chatbot?config=${encodeURIComponent(JSON.stringify(embedConfig))}" 
  width="${width}" 
  height="${height}px" 
  frameborder="0" 
  scrolling="auto"
  style="border: none; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1);">
</iframe>`;
    }
  };

  const openDeployModal = () => {
    linkGenModalRef.current?.openModal({
      _id: 'chatbot-embed',
      title: config.title,
      dataTitle: config.title
    });
  };

  // Load configurations list and initial config on component mount
  useEffect(() => {
    loadConfigurationsList();
  }, []);

  // Reload knowledge groups from localStorage when they change
  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const handleStorageChange = () => {
        try {
          const saved = localStorage.getItem('knowledgeGroups');
          if (saved) {
            setKnowledgeGroups(JSON.parse(saved));
          } else {
            setKnowledgeGroups([]);
          }
        } catch (e) {
          console.error('Failed to reload knowledge groups:', e);
        }
      };

      // Listen for storage changes
      window.addEventListener('storage', handleStorageChange);
      
      // Also check periodically (in case same-tab changes)
      const interval = setInterval(handleStorageChange, 2000);

      return () => {
        window.removeEventListener('storage', handleStorageChange);
        clearInterval(interval);
      };
    }
  }, []);

  // Load config when selectedConfigId changes (but not on initial mount to avoid double load)
  useEffect(() => {
    if (selectedConfigId && !isCreatingNew) {
      loadConfiguration(selectedConfigId);
    }
  }, [selectedConfigId]);

  const ConfigSection = ({ title, children }: { title: string, children: React.ReactNode }) => (
    <Card style={{ marginBottom: 20, padding: 16 }}>
      <Text style={{ fontSize: 18, fontWeight: '600', marginBottom: 12, color: '#1f2937' }}>
        {title}
      </Text>
      {children}
    </Card>
  );

  const ConfigField = ({ label, children }: { label: string, children: React.ReactNode }) => (
    <View style={{ marginBottom: 16 }}>
      <Text style={{ fontSize: 14, fontWeight: '500', marginBottom: 8, color: '#374151' }}>
        {label}
      </Text>
      {children}
    </View>
  );


  // Sidebar component for configs list
  const ConfigsSidebar = () => (
    <View
      style={{
        width: sidebarCollapsed ? 60 : 280,
        backgroundColor: '#f8fafc',
        borderRightWidth: 1,
        borderRightColor: '#e5e7eb',
        paddingTop: 20,
        paddingBottom: 20,
        height: '100%',
        ...(Platform.OS === 'web' && {
          transition: 'width 0.3s ease',
        }),
      }}
    >
      <View style={{ paddingHorizontal: 12, marginBottom: 16 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          {!sidebarCollapsed && (
            <Text style={{ fontSize: 18, fontWeight: '700', color: '#1f2937' }}>
              Configurations
            </Text>
          )}
          <TouchableOpacity
            onPress={() => setSidebarCollapsed(!sidebarCollapsed)}
            style={{
              padding: 8,
              borderRadius: 6,
              backgroundColor: '#ffffff',
              borderWidth: 1,
              borderColor: '#e5e7eb',
            }}
          >
            <Feather
              name={sidebarCollapsed ? 'chevron-right' : 'chevron-left'}
              size={16}
              color="#6b7280"
            />
          </TouchableOpacity>
        </View>
        {!sidebarCollapsed && (
          <TouchableOpacity
            onPress={() => handleConfigSelect('new')}
            disabled={!hasActiveSubscription && configs.length > 0}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              padding: 12,
              borderRadius: 8,
              backgroundColor: (!hasActiveSubscription && configs.length > 0) ? '#f3f4f6' : '#667eea',
              opacity: (!hasActiveSubscription && configs.length > 0) ? 0.6 : 1,
              marginBottom: 12,
            }}
          >
            <Feather name="plus" size={16} color={(!hasActiveSubscription && configs.length > 0) ? '#9ca3af' : 'white'} />
            <Text style={{ color: (!hasActiveSubscription && configs.length > 0) ? '#9ca3af' : 'white', fontSize: 14, fontWeight: '600' }}>
              New Config
            </Text>
          </TouchableOpacity>
        )}
      </View>

      <ScrollView 
        showsVerticalScrollIndicator={false} 
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingBottom: 20 }}
      >
        {configs.length === 0 ? (
          <View style={{ padding: 16, alignItems: 'center' }}>
            {!sidebarCollapsed && (
              <Text style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>
                No configurations yet. Create your first one!
              </Text>
            )}
          </View>
        ) : (
          configs.map((cfg) => (
            <Pressable
              key={cfg._id}
              onPress={() => handleConfigSelect(cfg._id)}
              style={{
                padding: 12,
                marginHorizontal: 8,
                marginBottom: 8,
                borderRadius: 8,
                backgroundColor: selectedConfigId === cfg._id ? '#667eea' : '#ffffff',
                borderWidth: 1,
                borderColor: selectedConfigId === cfg._id ? '#667eea' : '#e5e7eb',
                ...(Platform.OS === 'web' && {
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }),
              }}
            >
              {sidebarCollapsed ? (
                <View style={{ alignItems: 'center' }}>
                  <MaterialIcons
                    name="settings"
                    size={20}
                    color={selectedConfigId === cfg._id ? 'white' : '#6b7280'}
                  />
                </View>
              ) : (
                <View>
                  <Text
                    style={{
                      fontSize: 14,
                      fontWeight: '600',
                      color: selectedConfigId === cfg._id ? 'white' : '#1f2937',
                      marginBottom: 4,
                    }}
                    numberOfLines={1}
                  >
                    {cfg.title || 'Untitled'}
                  </Text>
                  {cfg.description && (
                    <Text
                      style={{
                        fontSize: 12,
                        color: selectedConfigId === cfg._id ? 'rgba(255,255,255,0.8)' : '#6b7280',
                      }}
                      numberOfLines={2}
                    >
                      {cfg.description}
                    </Text>
                  )}
                </View>
              )}
            </Pressable>
          ))
        )}
      </ScrollView>

      {!sidebarCollapsed && !hasActiveSubscription && configs.length > 0 && (
        <View
          style={{
            margin: 12,
            padding: 12,
            backgroundColor: '#fef3c7',
            borderRadius: 8,
            borderWidth: 1,
            borderColor: '#fbbf24',
          }}
        >
          <Text style={{ fontSize: 11, color: '#92400e', fontWeight: '500', textAlign: 'center' }}>
            Upgrade to create multiple configs
          </Text>
        </View>
      )}
    </View>
  );

  return (
    <XStack
      style={{
        flex: 1,
        width: "100%",
        alignSelf: 'stretch',
        paddingTop: "2vh",
        paddingBottom: 80,
        minWidth: "100%",
        maxWidth: "100%",
        gap: 0
      }}
    >
      {/* Configs Sidebar */}
      <ConfigsSidebar />

      {/* Configuration Panel */}
      <ScrollView showsVerticalScrollIndicator={false} style={{ flex: 1, paddingHorizontal: 16 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <Text style={{ fontSize: 24, fontWeight: '700', color: '#1f2937' }}>
            ChatBot Configuration
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            {isLoading && (
              <Text style={{ fontSize: 14, color: '#6b7280', fontStyle: 'italic' }}>
                Loading...
              </Text>
            )}
            <TouchableOpacity
              onPress={openDeployModal}
              disabled={isCreatingNew && !config.title}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: (isCreatingNew && !config.title) ? '#9ca3af' : '#667eea',
                paddingHorizontal: 16,
                paddingVertical: 10,
                borderRadius: 8,
                shadowColor: '#667eea',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.3,
                shadowRadius: 8,
                elevation: 4,
                opacity: (isCreatingNew && !config.title) ? 0.6 : 1,
              }}
            >
              <Feather name="upload-cloud" size={16} color="white" style={{ marginRight: 8 }} />
              <Text style={{ color: 'white', fontSize: 14, fontWeight: '600' }}>
                Deploy
              </Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Current Configuration Info */}
        {selectedConfigId && !isCreatingNew && (
          <Card style={{ marginBottom: 20, padding: 16 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <Text style={{ fontSize: 16, fontWeight: '600', color: '#1f2937' }}>
                {configs.find(c => c._id === selectedConfigId)?.title || 'Configuration'}
              </Text>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                <TouchableOpacity
                  onPress={() => {
                    const selectedConfig = configs.find(c => c._id === selectedConfigId);
                    if (selectedConfig) {
                      Alert.alert(
                        'Configuration Info',
                        `Title: ${selectedConfig.title}\n${selectedConfig.description ? `Description: ${selectedConfig.description}\n` : ''}${selectedConfig.createdAt ? `Created: ${new Date(selectedConfig.createdAt).toLocaleDateString()}\n` : ''}${selectedConfig.updatedAt ? `Updated: ${new Date(selectedConfig.updatedAt).toLocaleDateString()}` : ''}`
                      );
                    }
                  }}
                  style={{
                    padding: 8,
                    borderRadius: 6,
                    backgroundColor: '#f3f4f6',
                  }}
                >
                  <Feather name="info" size={16} color="#6b7280" />
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={() => deleteConfiguration(selectedConfigId)}
                  style={{
                    padding: 8,
                    borderRadius: 6,
                    backgroundColor: '#fee2e2',
                  }}
                >
                  <Feather name="trash-2" size={16} color="#ef4444" />
                </TouchableOpacity>
              </View>
            </View>
          </Card>
        )}

        {isCreatingNew && (
          <Card style={{ marginBottom: 20, padding: 16, backgroundColor: '#eff6ff', borderWidth: 1, borderColor: '#3b82f6' }}>
            <Text style={{ fontSize: 14, color: '#1e40af', fontWeight: '500' }}>
              Creating new configuration. Fill in the details below and click "Save Configuration" to create it.
            </Text>
          </Card>
        )}

        {/* Basic Settings */}
        <ConfigSection title="Basic Settings">
          <ConfigField label="ChatBot Title">
            <TextInput
              value={config.title}
              onChangeText={(text) => updateConfig('title', text)}
              style={{
                borderWidth: 1,
                borderColor: '#d1d5db',
                borderRadius: 8,
                padding: 12,
                fontSize: 14,
                backgroundColor: 'white'
              }}
              placeholder="Enter chatbot title"
            />
          </ConfigField>

          <ConfigField label="Description">
            <TextInput
              value={config.description}
              onChangeText={(text) => updateConfig('description', text)}
              style={{
                borderWidth: 1,
                borderColor: '#d1d5db',
                borderRadius: 8,
                padding: 12,
                fontSize: 14,
                backgroundColor: 'white',
                height: 80,
                textAlignVertical: 'top'
              }}
              placeholder="Describe your chatbot's purpose"
              multiline
            />
          </ConfigField>

          <ConfigField label="Welcome Message">
            <TextInput
              value={config.welcomeMessage}
              onChangeText={(text) => updateConfig('welcomeMessage', text)}
              style={{
                borderWidth: 1,
                borderColor: '#d1d5db',
                borderRadius: 8,
                padding: 12,
                fontSize: 14,
                backgroundColor: 'white'
              }}
              placeholder="First message users will see"
            />
          </ConfigField>
        </ConfigSection>

        {/* Behavior Settings */}
        <ConfigSection title="Behavior Settings">
          <ConfigField label="System Prompt">
            <TextInput
              value={config.systemPrompt}
              onChangeText={(text) => updateConfig('systemPrompt', text)}
              style={{
                borderWidth: 1,
                borderColor: '#d1d5db',
                borderRadius: 8,
                padding: 12,
                fontSize: 14,
                backgroundColor: 'white',
                height: 100,
                textAlignVertical: 'top'
              }}
              placeholder="Define how the chatbot should behave"
              multiline
            />
          </ConfigField>

          <ConfigField label="Response Style">
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {(['concise', 'detailed', 'step-by-step'] as const).map((style) => (
                <TouchableOpacity
                  key={style}
                  onPress={() => updateConfig('responseStyle', style)}
                  style={{
                    paddingHorizontal: 16,
                    paddingVertical: 8,
                    borderRadius: 20,
                    backgroundColor: config.responseStyle === style ? '#667eea' : '#f3f4f6',
                    borderWidth: 1,
                    borderColor: config.responseStyle === style ? '#667eea' : '#d1d5db'
                  }}
                >
                  <Text style={{
                    color: config.responseStyle === style ? 'white' : '#374151',
                    fontSize: 12,
                    fontWeight: '500'
                  }}>
                    {style.charAt(0).toUpperCase() + style.slice(1).replace('-', ' ')}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </ConfigField>

          <ConfigField label="Memory Enabled">
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text style={{ fontSize: 14, color: '#374151' }}>
                Remember conversation history
              </Text>
              <Switch
                value={config.memoryEnabled}
                onValueChange={(value) => updateConfig('memoryEnabled', value)}
                trackColor={{ false: '#d1d5db', true: '#667eea' }}
                thumbColor={config.memoryEnabled ? 'white' : '#f4f3f4'}
              />
            </View>
          </ConfigField>

          <ConfigField label="Auto Summarize">
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text style={{ fontSize: 14, color: '#374151' }}>
                Automatically summarize long conversations
              </Text>
              <Switch
                value={config.autoSummarize}
                onValueChange={(value) => updateConfig('autoSummarize', value)}
                trackColor={{ false: '#d1d5db', true: '#667eea' }}
                thumbColor={config.autoSummarize ? 'white' : '#f4f3f4'}
              />
            </View>
          </ConfigField>
        </ConfigSection>

        {/* Parameters */}
        <ConfigSection title="AI Parameters">
          <ConfigField label={`Temperature: ${config.temperature.toFixed(1)}`}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <Text style={{ fontSize: 12, color: '#6b7280', minWidth: 30 }}>0</Text>
              <View style={{ flex: 1, height: 20, backgroundColor: '#e5e7eb', borderRadius: 10, position: 'relative' }}>
                <View
                  style={{
                    position: 'absolute',
                    left: `${(config.temperature / 2) * 100}%`,
                    top: -6,
                    width: 20,
                    height: 20,
                    backgroundColor: '#667eea',
                    borderRadius: 10,
                    transform: [{ translateX: -10 }]
                  }}
                />
              </View>
              <Text style={{ fontSize: 12, color: '#6b7280', minWidth: 30 }}>2</Text>
            </View>
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
              {[0, 0.5, 1, 1.5, 2].map((value) => (
                <TouchableOpacity
                  key={value}
                  onPress={() => updateConfig('temperature', value)}
                  style={{
                    flex: 1,
                    paddingVertical: 8,
                    backgroundColor: Math.abs(config.temperature - value) < 0.1 ? '#667eea' : '#f3f4f6',
                    borderRadius: 6,
                    alignItems: 'center'
                  }}
                >
                  <Text style={{
                    color: Math.abs(config.temperature - value) < 0.1 ? 'white' : '#374151',
                    fontSize: 12,
                    fontWeight: '500'
                  }}>
                    {value}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            <Text style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
              Lower = more focused, Higher = more creative
            </Text>
          </ConfigField>

          <ConfigField label={`Max Tokens: ${config.maxTokens}`}>
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
              {[512, 1024, 2048, 3072, 4096].map((value) => (
                <TouchableOpacity
                  key={value}
                  onPress={() => updateConfig('maxTokens', value)}
                  style={{
                    flex: 1,
                    paddingVertical: 8,
                    backgroundColor: config.maxTokens === value ? '#667eea' : '#f3f4f6',
                    borderRadius: 6,
                    alignItems: 'center'
                  }}
                >
                  <Text style={{
                    color: config.maxTokens === value ? 'white' : '#374151',
                    fontSize: 12,
                    fontWeight: '500'
                  }}>
                    {value > 1000 ? `${value / 1000}k` : value}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </ConfigField>
        </ConfigSection>

        {/* Appearance */}
        <ConfigSection title="Appearance">
          <ConfigField label="Theme">
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {([
                { name: 'light', primaryColor: '#667eea', backgroundColor: '#ffffff', textColor: '#333333' },
                { name: 'dark', primaryColor: '#1f2937', backgroundColor: '#111827', textColor: '#f9fafb' },
                { name: 'auto', primaryColor: '#6366f1', backgroundColor: '#f8fafc', textColor: '#374151' }
              ] as const).map((theme) => (
                <TouchableOpacity
                  key={theme.name}
                  onPress={() => updateConfig('theme', theme)}
                  style={{
                    paddingHorizontal: 16,
                    paddingVertical: 8,
                    borderRadius: 20,
                    backgroundColor: config.theme.primaryColor === theme.primaryColor ? '#667eea' : '#f3f4f6',
                    borderWidth: 1,
                    borderColor: config.theme.primaryColor === theme.primaryColor ? '#667eea' : '#d1d5db'
                  }}
                >
                  <Text style={{
                    color: config.theme.primaryColor === theme.primaryColor ? 'white' : '#374151',
                    fontSize: 12,
                    fontWeight: '500'
                  }}>
                    {theme.name.charAt(0).toUpperCase() + theme.name.slice(1)}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </ConfigField>

          <ConfigField label="Position">
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
              {(['bottom-right', 'bottom-left', 'top-right', 'top-left'] as const).map((position) => (
                <TouchableOpacity
                  key={position}
                  onPress={() => updateConfig('position', position)}
                  style={{
                    paddingHorizontal: 12,
                    paddingVertical: 6,
                    borderRadius: 16,
                    backgroundColor: config.position === position ? '#667eea' : '#f3f4f6',
                    borderWidth: 1,
                    borderColor: config.position === position ? '#667eea' : '#d1d5db'
                  }}
                >
                  <Text style={{
                    color: config.position === position ? 'white' : '#374151',
                    fontSize: 11,
                    fontWeight: '500'
                  }}>
                    {position.replace('-', ' ')}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </ConfigField>

          <ConfigField label="Show Powered By">
            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text style={{ fontSize: 14, color: '#374151' }}>
                Display "Powered by SmartSaaS"
              </Text>
              <Switch
                value={config.integration.showPoweredBy}
                onValueChange={(value) => setConfig(prev => ({
                  ...prev,
                  integration: { ...prev.integration, showPoweredBy: value }
                }))}
                trackColor={{ false: '#d1d5db', true: '#667eea' }}
                thumbColor={config.integration.showPoweredBy ? 'white' : '#f4f3f4'}
              />
            </View>
          </ConfigField>
        </ConfigSection>

        {/* Knowledge Base Settings */}
        <ConfigSection title="Knowledge Base">
          <ConfigField label="Link Knowledge Base To">
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {(['userId', 'companyId'] as const).map((linkType) => (
                <TouchableOpacity
                  key={linkType}
                  onPress={() => setConfig(prev => ({
                    ...prev,
                    knowledgeBase: { ...prev.knowledgeBase, linkedTo: linkType }
                  }))}
                  style={{
                    flex: 1,
                    paddingHorizontal: 16,
                    paddingVertical: 8,
                    borderRadius: 8,
                    backgroundColor: config.knowledgeBase.linkedTo === linkType ? '#667eea' : '#f3f4f6',
                    borderWidth: 1,
                    borderColor: config.knowledgeBase.linkedTo === linkType ? '#667eea' : '#d1d5db'
                  }}
                >
                  <Text style={{
                    color: config.knowledgeBase.linkedTo === linkType ? 'white' : '#374151',
                    fontSize: 12,
                    fontWeight: '500',
                    textAlign: 'center'
                  }}>
                    {linkType === 'userId' ? 'User' : 'Company'}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            <Text style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
              Select whether to link to user's or company's knowledge base
            </Text>
          </ConfigField>

          <ConfigField label="Knowledge Base Scope">
            <View style={{ flexDirection: 'row', gap: 8, marginBottom: 12 }}>
              {(['entire', 'groups'] as const).map((scope) => (
                <TouchableOpacity
                  key={scope}
                  onPress={() => setConfig(prev => ({
                    ...prev,
                    knowledgeBase: {
                      ...prev.knowledgeBase,
                      scope,
                      selectedGroups: scope === 'groups' ? (prev.knowledgeBase.selectedGroups || []) : []
                    }
                  }))}
                  style={{
                    flex: 1,
                    paddingHorizontal: 16,
                    paddingVertical: 8,
                    borderRadius: 8,
                    backgroundColor: config.knowledgeBase.scope === scope ? '#667eea' : '#f3f4f6',
                    borderWidth: 1,
                    borderColor: config.knowledgeBase.scope === scope ? '#667eea' : '#d1d5db'
                  }}
                >
                  <Text style={{
                    color: config.knowledgeBase.scope === scope ? 'white' : '#374151',
                    fontSize: 12,
                    fontWeight: '500',
                    textAlign: 'center'
                  }}>
                    {scope === 'entire' ? 'Entire KB' : 'Groups'}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>

            {config.knowledgeBase.scope === 'groups' && (
              <View style={{ marginTop: 12 }}>
                <Text style={{ fontSize: 14, fontWeight: '500', marginBottom: 8, color: '#374151' }}>
                  Select Groups
                </Text>
                {knowledgeGroups.length === 0 ? (
                  <View style={{
                    padding: 16,
                    backgroundColor: '#f9fafb',
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: '#e5e7eb',
                    borderStyle: 'dashed'
                  }}>
                    <Text style={{ fontSize: 12, color: '#6b7280', textAlign: 'center' }}>
                      No knowledge groups available. Create groups in the Knowledge Base screen first.
                    </Text>
                  </View>
                ) : (
                  <View style={{ gap: 8 }}>
                    {knowledgeGroups.map((group) => {
                      const isSelected = config.knowledgeBase.selectedGroups?.includes(group.id) || false;
                      return (
                        <TouchableOpacity
                          key={group.id}
                          onPress={() => {
                            const currentGroups = config.knowledgeBase.selectedGroups || [];
                            const newGroups = isSelected
                              ? currentGroups.filter(id => id !== group.id)
                              : [...currentGroups, group.id];
                            setConfig(prev => ({
                              ...prev,
                              knowledgeBase: {
                                ...prev.knowledgeBase,
                                selectedGroups: newGroups
                              }
                            }));
                          }}
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            padding: 12,
                            borderRadius: 8,
                            backgroundColor: isSelected ? '#eff6ff' : '#ffffff',
                            borderWidth: 1,
                            borderColor: isSelected ? '#3b82f6' : '#e5e7eb'
                          }}
                        >
                          <View style={{
                            width: 20,
                            height: 20,
                            borderRadius: 4,
                            borderWidth: 2,
                            borderColor: isSelected ? '#3b82f6' : '#d1d5db',
                            backgroundColor: isSelected ? '#3b82f6' : 'transparent',
                            marginRight: 12,
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {isSelected && (
                              <Feather name="check" size={12} color="white" />
                            )}
                          </View>
                          <View style={{ flex: 1 }}>
                            <Text style={{
                              fontSize: 14,
                              fontWeight: '600',
                              color: '#1f2937',
                              marginBottom: 2
                            }}>
                              {group.name}
                            </Text>
                            <Text style={{
                              fontSize: 12,
                              color: '#6b7280'
                            }}>
                              {group.itemIds.length} {group.itemIds.length === 1 ? 'item' : 'items'}
                            </Text>
                          </View>
                        </TouchableOpacity>
                      );
                    })}
                  </View>
                )}
              </View>
            )}
          </ConfigField>
        </ConfigSection>

        {/* Action Buttons */}
        <View style={{ flexDirection: 'row', gap: 12, marginBottom: 20 }}>
          <MotiPressable
            onPress={saveConfiguration}
            disabled={isSaving}
            style={{
              flex: 1,
              paddingVertical: 14,
              paddingHorizontal: 24,
              borderRadius: 12,
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 48,
              overflow: 'hidden',
            }}
            animate={{
              backgroundColor: saveStatus?.success
                ? '#10b981'
                : saveStatus?.error
                ? '#ef4444'
                : isSaving
                ? '#9ca3af'
                : '#667eea',
              opacity: isSaving ? 0.8 : 1,
              scale: isSaving ? 0.98 : 1,
            }}
            transition={{
              type: 'timing',
              duration: 200,
            }}
          >
            {saveStatus?.success ? (
              <Animatable.View
                animation="bounceIn"
                duration={600}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
              >
                <Animatable.Text
                  animation="bounceIn"
                  duration={600}
                  style={{ color: 'white', fontSize: 18 }}
                >
                  ✅
                </Animatable.Text>
                <Text style={{ color: 'white', fontSize: 16, fontWeight: '600' }}>
                  Saved!
                </Text>
              </Animatable.View>
            ) : saveStatus?.error ? (
              <Animatable.View
                animation="shake"
                duration={400}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
              >
                <Text style={{ color: 'white', fontSize: 18 }}>❌</Text>
                <Text style={{ color: 'white', fontSize: 16, fontWeight: '600' }}>
                  Error
                </Text>
              </Animatable.View>
            ) : isSaving ? (
              <Animatable.View
                animation="fadeIn"
                iterationCount="infinite"
                duration={800}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
              >
                <Spinner size="small" color="white" />
                <Text style={{ color: 'white', fontSize: 16, fontWeight: '600' }}>
                  Saving...
                </Text>
              </Animatable.View>
            ) : (
              <Animatable.View
                animation="fadeIn"
                duration={200}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
              >
                <Feather name="save" size={18} color="white" />
                <Text style={{ color: 'white', fontSize: 16, fontWeight: '600' }}>
                  Save Configuration
                </Text>
              </Animatable.View>
            )}
          </MotiPressable>

          <TouchableOpacity
            onPress={resetConfiguration}
            disabled={isSaving}
            style={{
              flex: 1,
              backgroundColor: isSaving ? '#9ca3af' : '#6b7280',
              paddingVertical: 12,
              borderRadius: 8,
              alignItems: 'center',
              opacity: isSaving ? 0.7 : 1,
              justifyContent: 'center',
              minHeight: 48,
            }}
          >
            <Text style={{ color: 'white', fontSize: 16, fontWeight: '600' }}>
              Reset
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Preview Panel */}
      <View style={{ flex: 1, paddingHorizontal: 16 }}>
        <Text style={{ fontSize: 24, fontWeight: '700', marginBottom: 20, color: '#1f2937' }}>
          Live Preview
        </Text>

        <View style={{
          flex: 1,
          backgroundColor: '#f8fafc',
          borderRadius: 12,
          overflow: 'hidden',
          borderWidth: 1,
          borderColor: '#e5e7eb'
        }}>
          <WebView
            key={previewKey}
            ref={webViewRef}
            source={{ html: generateEmbedHTML() }}
            style={{
              flex: 1,
              height: '100%'
            }}
            javaScriptEnabled={true}
            domStorageEnabled={true}
            startInLoadingState={true}
            scalesPageToFit={false}
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            onMessage={(event) => {
              // Handle messages from WebView if needed
              console.log('Message from WebView:', event.nativeEvent.data);
            }}
            onError={(syntheticEvent) => {
              const { nativeEvent } = syntheticEvent;
              console.warn('WebView error: ', nativeEvent);
            }}
            onHttpError={(syntheticEvent) => {
              const { nativeEvent } = syntheticEvent;
              console.warn('WebView HTTP error: ', nativeEvent);
            }}
            renderLoading={() => (
              <View style={{
                flex: 1,
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#f8fafc',
                height: '100%'
              }}>
                <Text style={{ color: '#6b7280', fontSize: 16 }}>Loading preview...</Text>
              </View>
            )}
          />
        </View>
      </View>

      {/* LinkGenModal for ChatBot Embedding */}
      <LinkGenModal
        ref={linkGenModalRef}
        url="https://api.smartsaas.pro/embed/chatbot"
        caption="Deploy your ChatBot to any website with our embed code generator"
        heading="Deploy ChatBot"
        chatbotConfig={config}
        embedType="script"
      />
    </XStack>
  )
}

export default ChatBotScreen;